import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getScheduledPushInfo } from 'backend/pushes';
import {
	IScheduleViewContext,
	ScheduleViewContext,
} from '../../../../../scheduleViewContext/scheduleViewState/scheduleViewContext';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { EditScheduleActionTypes } from '../editScheduleModalActions';
import { editScheduleReducer } from './editScheduleModalReducer';
import {
	editScheduleInitialState,
	EditScheduleContext,
	TTypeOfEditScheduleModalWindow,
} from './editScheduleModalContext';
import { IEditScheduledPush, IGetScheduledPushInfo } from 'types';

const EditScheduleState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editScheduleReducer, editScheduleInitialState);
	const { IDForEdit } = useContext<IScheduleViewContext>(ScheduleViewContext);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const { response, loading, refetch } = useFetchData<IGetScheduledPushInfo, string>(
		IDForEdit ? getScheduledPushInfo : null,
		IDForEdit || '',
	);

	useEffect(() => {
		refetch();
	}, [IDForEdit]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditScheduleActionTypes.SET_ALL_DATA,
				payload: {
					id: response.data.id,
					type: response.data.type,
					status: response.data.status,
					scheduledDateTime: response.data.scheduledDateTime,
				},
			});
			dispatch({
				type: EditScheduleActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						schedule: response.data.scheduledDateTime,
					},
				},
			});
		}
	}, [response]);

	const changeSchedule = useCallback((value: string) => {
		dispatch({
			type: EditScheduleActionTypes.CHANGE_SCHEDULE,
			payload: {
				schedule: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IEditScheduledPush | null) => {
		dispatch({
			type: EditScheduleActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditScheduleModalWindow) => {
		dispatch({
			type: EditScheduleActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditScheduleActionTypes.RESET_FIELDS }), []);

	return (
		<EditScheduleContext.Provider
			value={{
				changeSchedule,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				initialFormData: state.initialFormData,
				scheduleID: state.scheduleID,
				pushType: state.pushType,
				schedule: state.schedule,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditScheduleContext.Provider>
	);
};

export default React.memo(EditScheduleState);
